import { LocalizationString } from '@celito.clients/assets';
import { ActionTypeEnum } from '@celito.clients/enums';
import { raiseErrorToast, successToast } from '@celito.clients/utils';
import { yupResolver } from '@hookform/resolvers/yup';
import { useEffect, useRef, useState } from 'react';
import { Control, useForm } from 'react-hook-form';
import * as yup from 'yup';

import { useListViewContext } from '../../hooks';
import {
  cancelExtensionRequest,
  createExtensionRequest,
} from '../../services/extension-request.service';
import { getObjectMetadata } from '../../services/get-object-metadata';
import {
  ExtensionRequestConfig,
  ExtensionRequestDialogProps,
} from './extension-request-dialog.model';
import { ExtensionRequestDialogView } from './extension-request-dialog.view';

// Define all possible form value fields (union of all forms)
type FormValues = {
  requestedDueDate?: Date;
  justification?: string;
  reasonForCancelation?: string;
  comments?: string;
  approvers?: { text: string; value: string }[];
};

// Create a dynamic schema that changes based on action type
const getSchemaForAction = (actionType: ActionTypeEnum) => {
  switch (actionType) {
    case ActionTypeEnum.REQUEST_EXTENSION:
      return yup.object().shape({
        requestedDueDate: yup
          .date()
          .required(LocalizationString.REQUIRED_MSG)
          .min(new Date(), 'Due date must be in the future'),
        justification: yup
          .string()
          .required(LocalizationString.REQUIRED_MSG)
          .min(
            10,
            'Please provide a more detailed justification for the extension request'
          )
          .max(4000, 'Max 4000 characters'),
        approvers: yup.array(),
      });

    case ActionTypeEnum.CANCEL_EXTENSION:
      return yup.object().shape({
        reasonForCancelation: yup
          .string()
          .required(LocalizationString.REQUIRED_MSG)
          .min(10, 'Please provide a detailed cancelation reason')
          .max(1000, 'Max 1000 characters'),
        comments: yup.string(),
      });
    default:
      return yup.object().shape({});
  }
};

export const ExtensionRequestDialogController = ({
  onClose,
  isOpen,
  rowItem,
  objectName,
  actionType,
  extensionRequestConfig: configFromProps,
}: ExtensionRequestDialogProps) => {
  const { fetchRowData, currentPageNumber, filters, sortConfig } =
    useListViewContext();

  const [isLoading, setIsLoading] = useState(false);
  const submitCountRef = useRef(0);
  const [extensionRequestConfig, setExtensionRequestConfig] =
    useState<ExtensionRequestConfig | null>(null);
  const [currentDueDate, setCurrentDueDate] = useState<Date | null>(null);

  // Create a single form with a dynamic schema based on action type
  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: yupResolver(getSchemaForAction(actionType)),
    mode: 'onChange',
    reValidateMode: 'onChange',
    criteriaMode: 'all',
  });

  // Reset the form and update the resolver when action type changes
  useEffect(() => {
    reset();
  }, [actionType, reset]);

  // Fetch object definition to get extension request config if not provided in props
  useEffect(() => {
    const fetchObjectDefinition = async () => {
      try {
        setIsLoading(true);
        const objectMetadata = await getObjectMetadata(objectName);

        if (objectName !== 'extension_request__s') {
          if (objectMetadata && objectMetadata.extensionRequestConfig) {
            setExtensionRequestConfig(objectMetadata.extensionRequestConfig);
          } else {
            raiseErrorToast(
              new Error('This object does not support extension requests')
            );
            onClose();
          }
        }
      } catch (error) {
        raiseErrorToast(error);
        onClose();
      } finally {
        setIsLoading(false);
      }
    };

    if (isOpen && objectName) {
      if (configFromProps) {
        setExtensionRequestConfig(configFromProps);
      } else {
        fetchObjectDefinition();
      }
    }
  }, [isOpen, objectName, configFromProps, onClose]);

  // Reset form when dialog closes or action type changes
  useEffect(() => {
    if (!isOpen) {
      reset();
    }
  }, [isOpen, actionType, reset]);

  // Set initial values based on current due date and action type
  useEffect(() => {
    if (rowItem && extensionRequestConfig?.dueDateColumnName) {
      const dueDateValue = rowItem[extensionRequestConfig.dueDateColumnName];
      if (dueDateValue) {
        setCurrentDueDate(dueDateValue);

        // Set appropriate initial values based on action type
        if (actionType === ActionTypeEnum.REQUEST_EXTENSION) {
          // Prepopulate approvers from row record attributes if configured
          if (
            extensionRequestConfig.approversColumnName &&
            extensionRequestConfig.approversColumnName.length > 0
          ) {
            const prepopulatedApprovers: { text: string; value: string }[] = [];

            for (const attributeKey of extensionRequestConfig.approversColumnName) {
              const attributeValue = rowItem[attributeKey];
              if (attributeValue) {
                // Handle both single values and arrays
                const approverIds = Array.isArray(attributeValue)
                  ? attributeValue
                  : [attributeValue];

                for (const approverId of approverIds) {
                  if (approverId) {
                    let approverValue: string;
                    let approverText: string;

                    if (typeof approverId === 'string') {
                      approverValue = approverId;
                      approverText = approverId;
                    } else if (
                      typeof approverId === 'object' &&
                      (approverId.name || approverId.value) &&
                      (approverId.label || approverId.text)
                    ) {
                      approverValue = approverId.name || approverId.value;
                      approverText = approverId.label || approverId.text;
                    } else {
                      return; // Skip invalid entries
                    }
                    // Add to prepopulated list if not already present
                    if (
                      !prepopulatedApprovers.some(
                        (approver) => approver.value === approverValue
                      )
                    ) {
                      prepopulatedApprovers.push({
                        value: approverValue,
                        text: approverText, // The PeoplePicker will resolve the display name
                      });
                    }
                  }
                }
              }
            }

            if (prepopulatedApprovers.length > 0) {
              setValue('approvers', prepopulatedApprovers);
            }
          }
        }
      }
    }
  }, [rowItem, extensionRequestConfig, actionType, setValue]);

  const onFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    handleSubmit(onSubmitHandler)();
  };

  const onSubmitHandler = async (data: FormValues) => {
    if (submitCountRef.current + 1 > 1) return;
    submitCountRef.current = submitCountRef.current + 1;
    setIsLoading(true);

    try {
      switch (actionType) {
        case ActionTypeEnum.REQUEST_EXTENSION:
          if (data.requestedDueDate && data.justification) {
            await createExtensionRequest({
              objectName,
              objectRecordId: rowItem.id,
              objectRecordName: rowItem.name || '',
              requestedDueDate: data.requestedDueDate.toISOString(),
              justification: data.justification,
              approvers: data.approvers?.map((approver) => approver.value),
            });
          } else {
            throw new Error('Required fields missing');
          }
          break;

        case ActionTypeEnum.CANCEL_EXTENSION:
          if (!rowItem.name) {
            throw new Error('Extension request ID is required');
          }
          if (data.reasonForCancelation) {
            await cancelExtensionRequest(rowItem.name, {
              reasonForCancelation: data.reasonForCancelation,
              comments: data.comments,
            });
          } else {
            throw new Error('Required fields missing');
          }
          break;
      }

      // Refresh data after successful action
      fetchRowData(objectName, currentPageNumber, filters, sortConfig);

      successToast({
        message: `Extension request ${getSuccessMessage()}`,
      });

      onClose();
    } catch (error) {
      raiseErrorToast(error);
    } finally {
      submitCountRef.current = 0;
      setIsLoading(false);
    }
  };

  const getSuccessMessage = () => {
    switch (actionType) {
      case ActionTypeEnum.REQUEST_EXTENSION:
        return 'submitted successfully';

      case ActionTypeEnum.CANCEL_EXTENSION:
        return 'cancelled successfully';
      default:
        return 'processed successfully';
    }
  };

  // Handler functions have been moved and combined in the onSubmitHandler function

  const onPopupClose = () => {
    reset();
    onClose();
  };

  return (
    <ExtensionRequestDialogView
      onClose={onClose}
      isOpen={isOpen}
      rowItem={rowItem}
      objectName={objectName}
      actionType={actionType}
      control={control as Control<FormValues>}
      onFormSubmit={onFormSubmit}
      isLoading={isLoading}
      onPopupClose={onPopupClose}
      currentDueDate={currentDueDate}
      extensionRequestConfig={extensionRequestConfig}
      errors={errors}
    />
  );
};
